<script lang="ts">
	import { browser } from '$app/environment';
	import { Icon } from '$lib/components/atoms/Icon';
	import { LinkTo } from '$lib/components/atoms/Link';
	import { lazyLoader } from '$lib/utils/lazyloader';
  import { fade } from 'svelte/transition';

  export let data: Array<{
    title: string;
    subtitle: string;
    description?: string;
    image: string;
    srcset?: string;
    url?: string;
  }>;
  export let detailLabel = 'Vedi di più';
  export let detailStyle: 'centered' | 'right' = 'centered';
  export let imageStyle: 'square' | 'auto' = 'square';

  let currentItem: typeof data[0] | undefined = data.length
    ? data[0]
    : undefined;
  let listEl: HTMLElement;
  let canScrollUp = false;
  let canScrollDown = false;

  const removePathId = (str: string) => {
    if (str) {
      const pathData = str.split("-");
      const pathId = pathData[pathData.length - 1];
      if (parseInt(pathId) && parseInt(pathId) > 0) {
        return str.substring(0, str.length - pathId.length - 1);
      } else return str;
    }
  };

  $: if (listEl) updateNavigationStatus();

  const scrollItems = (mode: 'up' | 'down') => {
    const requestAnimationFrame =
      browser &&
      typeof window !== 'undefined' &&
      (window.requestAnimationFrame ||
        window.mozRequestAnimationFrame ||
        window.webkitRequestAnimationFrame ||
        window.msRequestAnimationFrame);

    let startTime: number | undefined;

    const step = (time: number) => {
      if (!startTime) startTime = time;

      listEl.scrollBy({
        top: mode === 'up' ? -4 : 4,
      });

      if (time - startTime < 210) {
        window.requestAnimationFrame(step);
      } else {
        updateNavigationStatus();
      }
    };

    window.requestAnimationFrame(step);
  };

  const updateNavigationStatus = () => {
    const scrollHeight = listEl.scrollHeight;
    const containerHeight = listEl.clientHeight;

    canScrollUp = listEl.scrollTop > 0;
    canScrollDown = listEl.scrollTop + containerHeight < scrollHeight;
  };
</script>

<svelte:window on:resize={updateNavigationStatus} />

<div class="items-carousel" class:v-grid={detailStyle === 'right'}>
  <div class="items-navigation">
    <ul class="items-list" bind:this={listEl}>
      {#each data as item}
        <li
          class="item-selector"
          aria-current={currentItem === item}
          on:mouseover={() => (currentItem = item)}
        >
          {item.title}
        </li>
      {/each}
    </ul>

    <div
      class="navigation-buttons"
      class:v-hidden={!canScrollDown && !canScrollUp}
    >
      <button
        class="navigation-btn v-prev"
        disabled={!canScrollUp}
        on:click={() => scrollItems('up')}
      >
        <span class="sr-only">Precedente</span>
        <span class="btn-icon">
          <Icon icon="chevronUp" />
        </span>
      </button>
      <button
        class="navigation-btn v-next"
        disabled={!canScrollDown}
        on:click={() => scrollItems('down')}
      >
        <span class="sr-only">Successivo</span>
        <span class="btn-icon">
          <Icon icon="chevronDown" />
        </span>
      </button>
    </div>
  </div>

  <div class="item-content">
    {#if currentItem}
      {#key currentItem}
        <div class="content-container" in:fade out:fade>
          <figure
            class="content-image"
            class:v-square={imageStyle === 'square'}
          >
            <img
              use:lazyLoader={currentItem.image}
              srcset={currentItem.srcset}
              alt={currentItem.title}
            />
          </figure>
          <h5 class="content-title">{currentItem.title}</h5>
          <p class="content-subtitle">{currentItem.subtitle}</p>
          {#if currentItem.description}
            <p class="content-description">{currentItem.description}</p>
          {/if}
          {#if currentItem.url}
            <div class="content-link">
              <LinkTo href={removePathId(currentItem.url)}>{detailLabel}</LinkTo>
            </div>
          {/if}
        </div>
      {/key}
    {/if}
  </div>
</div>

<style type="text/scss" lang="scss" scoped>
  .items-carousel {
    @apply flex flex-col;
    @apply py-4;
    @apply md:flex-row;

    &.v-grid {
      @apply md:grid md:grid-cols-12 md:grid-rows-1;
      @apply overflow-x-hidden;

      .items-navigation {
        @apply md:col-start-1 md:col-end-7;
        @apply md:row-start-1;
        @apply md:w-auto;
        @apply lg:col-end-5;
      }
      .item-content {
        @apply md:col-start-8 md:col-end-13;
        @apply md:row-start-1;
        @apply md:w-auto;
      }
      .content-container {
        @apply mx-0;
      }
    }
  }

  .items-navigation {
    @apply flex;
    @apply overflow-x-hidden;
    margin-left: calc(var(--page-margin) * -1);
    margin-right: calc(var(--page-margin) * -1);
    @apply md:mx-0;
    @apply md:max-h-128;
    @apply md:flex-col;
    @apply md:w-1/3;
    @apply lg:w-auto;
  }

  .items-list {
    @apply list-none;
    @apply flex justify-start;
    @apply space-x-6;
    @apply overflow-x-auto;
    @apply page-margin;
    @apply mb-6;
    @apply md:px-0;
    @apply md:overflow-x-hidden md:overflow-y-hidden;
    @apply md:flex-col md:flex-grow md:items-start;
    @apply md:space-x-0 md:space-y-7;

    &::-webkit-scrollbar {
      @apply hidden;
    }
  }

  .item-selector {
    @apply relative;
    @apply font-serif font-medium;
    @apply text-md whitespace-nowrap;
    @apply text-gray-900 text-opacity-50;
    @apply cursor-pointer;
    @apply transition-colors duration-200;
    @apply hover:text-opacity-100;
    @apply md:text-lg;
    @apply md:max-w-full md:flex-shrink-0;
    @apply md:truncate;

    &::after {
      content: '';
      @apply absolute bottom-0 inset-x-0;
      @apply h-px;
      @apply bg-current;
    }

    &[aria-current='true'] {
      @apply text-opacity-100;
    }
  }

  .navigation-buttons {
    @apply hidden md:flex;
    @apply mt-auto;
    @apply space-x-2;

    &.v-hidden {
      @apply invisible;
    }
  }

  .navigation-btn {
    @apply w-12 h-12;
    @apply border-2 border-gray-900 rounded-full;
    @apply text-gray-900;
    @apply focus:outline-none;
    @apply disabled:border-gray-300 disabled:text-gray-300;
    @apply disabled:cursor-default;

    > .btn-icon {
      @apply flex items-center justify-center;
      @apply m-auto;
      @apply transform translate-y-0;
      @apply transition-transform duration-200;
    }

    &:hover:not(:disabled) {
      &.v-prev > .btn-icon {
        @apply -translate-y-0.5;
      }
      &.v-next > .btn-icon {
        @apply translate-y-0.5;
      }
    }
  }

  .item-content {
    @apply flex-1;
    @apply grid grid-cols-1 grid-rows-1;
  }
  :global(.items-carousel .item-content > *) {
    @apply col-start-1 row-start-1;
  }

  .content-container {
    @apply flex flex-col;
    @apply md:mx-auto;
  }

  .content-image {
    @apply relative;
    @apply w-full md:w-96 md:max-w-full;
    @apply overflow-hidden;
    @apply mb-6 md:mb-8;

    > img {
      @apply w-full h-auto;
      @apply object-contain;
    }

    &.v-square {
      &::before {
        content: '';
        @apply block;
        @apply w-0 pb-full;
      }

      > img {
        @apply absolute inset-0;
        @apply w-full h-full;
        @apply object-cover object-center;
      }
    }
  }

  .content-title {
    @apply text-md;
    @apply md:text-2xl;
  }

  .content-subtitle {
    @apply text-gray-600;
    @apply uppercase tracking-widest;
    @apply mt-0.5 md:mt-1.5;
  }

  .content-description {
    @apply mt-4;
  }

  .content-link {
    @apply mt-4;
  }
</style>
